import React, { MouseEvent, ReactElement, useContext, useEffect, useState } from 'react';
import { useFeatureFlag } from '@providers/FeatureFlags';
import Buttons from '@components/Buttons';
import useNoodleApi from '@hooks/useNoodleApi';
import { updateWorkflow } from '@tsClient';
import Spacer from '@components/Spacer';
import FieldLabel from '@components/FormFields/FieldLabel';
import TextInput from '@components/FormFields/TextInput';
import * as ApiModels from '@typings/api-models';
import { AssignToPersonType, CONTEXT_TYPE } from '@typings/api-models';
import TriggerCard from '@components/TriggerCard';
import ActionCard from '@components/ActionCard';
import PlusCircle from '@components/Icons/PlusCircle';
import Gear from '@components/Icons/Gear';
import HandCoins from '@components/Icons/HandCoins';
import BracketsCurly from '@components/Icons/BracketsCurly';
import Users from '@components/Icons/Users';
import updateWorkflowStep from '@tsClient/workflows/updateWorkflowStep';
import createWorkflowStep from '@tsClient/workflows/createWorkflowStep';
import deleteWorkflowStep from '@tsClient/workflows/deleteWorkflowStep';
import updateWorkflowOwners from '@tsClient/workflows/updateWorkflowOwners';
import removeNullish from '@helpers/removeNullish';
import { SendMessageActionData } from '@components/EditSendMessageActionForm/EditSendMessageActionForm';
import ProgressIndicator from '@components/ProgressIndicator';
import CheckBox from '@components/DesignLibrary/Atoms/CheckBox';
import SelectInvoiceTemplate from '@components/SelectInvoiceTemplate';
import { createWorkflowInvoice, deleteWorkflowContext, deleteWorkflowInvoice, updateWorkflowInvoice } from '@tsClient/workflows';
import WorkflowInvoices from '@components/WorkflowInvoices';
import updateWorkflowContext from '@tsClient/workflows/updateWorkflowContext';
import AddContextForm from '@components/AddContextForm';
import CustomSelect from '@components/FormFields/CustomSelect';
import PriceInput from '@components/FormFields/PriceInput';
import Button from '@components/DesignLibrary/Button';
import CollapsibleArea from '@components/CollapsibleArea';
import EditWorkflowStepModal from '@modals/EditWorkflowStepModal';
import dashboardContext from '@layouts/DashboardLayout/DashboardContext';
import { isAttorney } from '@helpers/businessCategories';
import AddWorkflowThreadModal from '@modals/AddWorkflowThreadModal';
import EditWorkflowThreadTitleModal from '@modals/EditWorkflowThreadTitleModal';
import AssignCollaboratorsToAttachmentForm from '@components/AssignCollaboratorsToAttachmentForm';
import { getCreatorTeam } from '@tsClient/creators';
import { useToast } from '@hooks';
import { getCustomStatuses } from '@/tsClient/customStatuses';
import { TBody, TD, TH, THead, Table, TableRow } from '@components/DesignLibrary/Table';

import s from './EditWorkflowForm.module.scss';
import getAvailableTriggers from './getAvailableTriggers';
import ConditionsWidget from '../DesignLibrary/ConditionsWidget';
import WorkflowOptionSelect from '../WorkflowOptionSelect';
import Modal from '../Modal';

type ThisWorkflow =
  & Pick<ApiModels.Workflow,
    | 'id'
    | 'name'
    | 'hasGeneratedPdfs'
    | 'noodleFee'
    | 'estimatedTotalFees'
    | 'sendEmailToCreatorOnCompletion'
    | 'sendEmailToCreatorOnFirstPayment'
    | 'canInitiateAssociatedWorkflows'
    | 'type'
    | 'enableUscisUpdates'
    | 'enablePacerSubmissions'
  > & {
    /** @deprecated - use threads + stepMap instead */
    steps: Array<
      & Pick<ApiModels.WorkflowStep, 'id' | 'type'>
      & Parameters<typeof TriggerCard>[0]['trigger']
      & Parameters<typeof ActionCard>[0]['action']
      & Parameters<typeof getAvailableTriggers>[0]['step']
      & Parameters<typeof EditWorkflowStepModal>[0]['currentStep']
    >;
    owners: Array<Pick<ApiModels.WorkflowOwner, 'personId'>>;
    threads: Array<{
      rank: number;
      stepIds: string[];
      workflowStatuses: (string | null)[];
    }>;
    contexts: Array<Pick<ApiModels.WorkflowContext, 'id' | 'type' | 'label' | 'isTitleField' | 'isOptional' | 'options' | 'slug'> & {
      expression?: ApiModels.Expression | null;
      variables: Array<Pick<ApiModels.WorkflowContextVariable, 'variableName'>>;
    }>;
    workflowInvoices: Array<Pick<ApiModels.WorkflowInvoice, 'id' | 'title' | 'workflowInvoiceAssigneeIds'>
      & { expression?: ApiModels.Expression | null }>;
  }
  & Parameters<typeof getAvailableTriggers>[0]['workflow']
  & Parameters<typeof EditWorkflowStepModal>[0]['workflow'];

type Props = {
  workflow: ApiModels.CommonWorkflowResponse;
  initialMessage?: string | null;
  creatorTeamMembers: Pick<ApiModels.TeamMember, 'id' | 'name'>[];
  onSave: () => void;
  refetchWorkflow: () => Promise<void>;
  canEdit?: boolean;
};

const workflowTypeOptions = [
  {
    title: 'Basic',
    value: 'basic',
  },
  {
    title: 'Attorney',
    value: 'attorney-case',
  },
];

type SelectOption = Parameters<typeof WorkflowOptionSelect>[0]['currentOption'];

const EditWorkflowForm = ({ workflow: initialWorkflow, canEdit = true, creatorTeamMembers, refetchWorkflow, onSave }: Props): ReactElement => {
  const canEditNoodleApplicationFees = useFeatureFlag('can-edit-noodle-application-fees');
  const isUscisEnabled = useFeatureFlag('uscis');
  const isPacerEnabled = useFeatureFlag('pacer');
  const [workflow, setWorkflow] = useState<ApiModels.CommonWorkflowResponse>(initialWorkflow);
  const [isSaving, setIsSaving] = useState(false);
  const [addingStepAfterId, setAddingStepAfterId] = useState<string | null>(null);
  const [isAddInvoiceSectionOpen, setIsAddInvoiceSectionOpen] = useState(false);
  const [isAddContextSectionOpen, setAddContextSectionOpen] = useState(false);
  const [isAddThreadModalOpen, setAddThreadModalOpen] = useState(false);
  const [isEditThreadTitleModalOpen, setEditThreadTitleModalOpen] = useState(false);
  const [isEditWorkflowStepModalOpen, setEditWorkflowStepModalOpen] = useState(false);
  const [currentInvoiceTemplateId, setCurrentInvoiceTemplateId] = useState<string | null>(null);
  const [deletingContextId, setDeletingContextId] = useState('');
  const [deletingStep, setDeletingStep] = useState<ThisWorkflow['steps'][number]['id'] | null>(null);
  const [currentStep, setCurrentStep] = useState<ThisWorkflow['steps'][number] | null>(null);
  const [ownerPersonIds, setOwnerPersonIds] = useState<string[]>(initialWorkflow.owners.map((o) => o.personId));
  const [stepStatuses, setStepStatuses] = useState<Map<string, SelectOption>>(new Map());

  const { getData: createStepFn } = useNoodleApi(createWorkflowStep);
  const { getData: deleteStepFn } = useNoodleApi(deleteWorkflowStep);
  const { fetchingState: creatingInvoice, getData: createWorkflowInvoiceFn } = useNoodleApi(createWorkflowInvoice);
  const { getData: deleteWorkflowInvoiceFn } = useNoodleApi(deleteWorkflowInvoice);
  const { getData: updateWorkflowInvoiceFn } = useNoodleApi(updateWorkflowInvoice);
  const { getData: updateWorkflowStepFn } = useNoodleApi(updateWorkflowStep, { toastOnError: true });
  const { getData: updateWorkflowFn } = useNoodleApi(updateWorkflow, { toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Workflow saved successfully!'] });
  const { getData: updateWorkflowContextFn } = useNoodleApi(updateWorkflowContext, { toastOnError: true });
  const { getData: deleteWorkflowContextFn } = useNoodleApi(deleteWorkflowContext, { toastOnError: true });
  const {
    fetchingState: { isFetching: isAssigningOwners },
    getData: updateWorkflowOwnersFn,
  } = useNoodleApi(updateWorkflowOwners, { toastOnError: true });
  const { fetchingState: { isFetching: isFetchingTeamMembers }, getData: getTeamMembersFn, data: teamMembers } = useNoodleApi(getCreatorTeam);
  const { getData: getCreatorStatusesFn, data: customStatuses } = useNoodleApi(getCustomStatuses);

  const { creator } = useContext(dashboardContext);

  const handleWorkflowStatusChange = async (stepId: string, workflowStatus: SelectOption): Promise<void> => {
    const newStatus = workflowStatus === 'maintain-status' ? null : workflowStatus;

    await updateWorkflowStepFn({
      id: workflow.id,
      stepId,
      updates: { workflowStatus: newStatus },
    });

    setStepStatuses(prev => new Map(prev).set(stepId, workflowStatus));
  };

  useEffect(() => {
    setWorkflow(initialWorkflow);
  }, [initialWorkflow]);

  useEffect(() => {
    const map = new Map<string, SelectOption>();

    const firstThread = workflow.threads[0];
    if (firstThread) {
      firstThread.stepIds.forEach((stepId, index) => {
        const status = index === 0
          ? firstThread.workflowStatuses?.[index] || 'data-collection'
          : firstThread.workflowStatuses?.[index] || 'maintain-status';
        map.set(stepId, status as SelectOption);
      });
    }

    setStepStatuses(map);
  }, [workflow]);

  useEffect(() => {
    if (creator) {
      getTeamMembersFn({ creatorId: creator.id, page: 1, perPage: 1000 });
    }
  }, [getTeamMembersFn, creator]);

  useEffect(() => {
    if (creator) {
      getCreatorStatusesFn({ creatorId: creator.id, page: 1, perPage: 1000 });
    }
  }, [getCreatorStatusesFn, creator]);

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    const updates: Parameters<typeof updateWorkflowFn>[0]['updates'] = {
      canInitiateAssociatedWorkflows: workflow.canInitiateAssociatedWorkflows,
      enablePacerSubmissions: workflow.enablePacerSubmissions,
      enableUscisUpdates: workflow.enableUscisUpdates,
      hasGeneratedPdfs: workflow.hasGeneratedPdfs,
      name: workflow.name,
      sendEmailToCreatorOnCompletion: workflow.sendEmailToCreatorOnCompletion,
      sendEmailToCreatorOnFirstPayment: workflow.sendEmailToCreatorOnFirstPayment,
      type: workflow.type,
    };
    if (canEditNoodleApplicationFees) {
      updates.estimatedTotalFees = workflow.estimatedTotalFees;
      updates.noodleFee = workflow.noodleFee;
    }
    await updateWorkflowFn({
      updates,
      workflowId: workflow.id,
    });
    onSave();
    setIsSaving(false);
  };

  const handleAddContext = async (): Promise<void> => {
    await refetchWorkflow();
    setAddContextSectionOpen(false);
  };

  const deleteContext = async (contextId: string): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this context?')) {
      return;
    }
    setDeletingContextId(contextId);
    await deleteWorkflowContextFn({
      contextId,
      workflowId: workflow.id,
    });
    await refetchWorkflow();
    setDeletingContextId('');
  };

  const handleAddInvoice = async (): Promise<void> => {
    if (currentInvoiceTemplateId) {
      await createWorkflowInvoiceFn({
        invoiceTemplateId: currentInvoiceTemplateId,
        workflowId: workflow.id,
      });
      setIsAddInvoiceSectionOpen(false);
      refetchWorkflow();
    }
  };

  const handleDeleteInvoice = async (workflowInvoiceId: string): Promise<void> => {
    await deleteWorkflowInvoiceFn({
      id: workflowInvoiceId,
      workflowId: workflow.id,
    });
    refetchWorkflow();
  };

  const handleChangeName = (newValue: string): void => {
    setWorkflow(prev => ({ ...prev, name: newValue }));
  };

  const handleToggleHasGeneratedPdfs = (newValue: boolean): void => {
    setWorkflow(prev => ({ ...prev, hasGeneratedPdfs: newValue }));
  };

  const handleToggleSendEmailToCreatorOnCompletion = (newValue: boolean): void => {
    setWorkflow(prev => ({ ...prev, sendEmailToCreatorOnCompletion: newValue }));
  };

  const handleToggleEnableUscisUpdates = (newValue: boolean): void => {
    setWorkflow(prev => ({ ...prev, enableUscisUpdates: newValue }));
  };

  const handleToggleEnablePacerSubmissions = (newValue: boolean): void => {
    setWorkflow(prev => ({ ...prev, enablePacerSubmissions: newValue }));
  };

  const handleToggleSendEmailToCreatorOnFirstPayment = (newValue: boolean): void => {
    setWorkflow(prev => ({ ...prev, sendEmailToCreatorOnFirstPayment: newValue }));
  };

  const handleToggleCanInitiateAssociatedWorkflows = (newValue: boolean): void => {
    setWorkflow(prev => ({ ...prev, canInitiateAssociatedWorkflows: newValue }));
  };

  const handleToggleIsTitleField = async (wfContextId: string, newValue: boolean): Promise<void> => {
    await updateWorkflowContextFn({
      contextId: wfContextId,
      updates: { isTitleField: newValue },
      workflowId: workflow.id,
    });
    refetchWorkflow();
  };

  const handleToggleIsOptional = async (wfContextId: string, newValue: boolean): Promise<void> => {
    await updateWorkflowContextFn({
      contextId: wfContextId,
      updates: { isOptional: newValue },
      workflowId: workflow.id,
    });
    refetchWorkflow();
  };

  const handleToggleEnableInvoiceView = async (wfContextId: string, newValue: boolean): Promise<void> => {
    await updateWorkflowContextFn({
      contextId: wfContextId,
      updates: { enableInvoiceView: newValue },
      workflowId: workflow.id,
    });
    refetchWorkflow();
  };

  const handleChangeType = (newValue: string): void => {
    if (newValue === 'attorney-case' || newValue === 'basic') {
      setWorkflow(prev => ({ ...prev, type: newValue }));
    }
  };

  const handleChangeWorkflowNumberField = (field: 'noodleFee' | 'estimatedTotalFees', newValue: number): void => {
    setWorkflow(prev => ({ ...prev, [field]: newValue }));
  };

  const updateStep: Parameters<typeof TriggerCard>[0]['updateStep'] = async ({ stepId, updates }) => {
    setWorkflow({
      ...workflow,
      steps: workflow.steps.map(step => (step.id === stepId ? { ...step, ...updates } : step)),
    });
    await updateWorkflowStepFn({
      id: workflow.id,
      stepId,
      updates,
    });
  };

  const handleAddSteps = (stepId: string) => async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (addingStepAfterId) {
      return;
    }
    setAddingStepAfterId(stepId);
    const lastStep = workflow.steps.find(step => step.id === stepId);

    let trigger;
    if (lastStep && lastStep.type === ApiModels.StepType.TRIGGER) {
      trigger = lastStep;
    } else {
      const triggerToCreate = workflow.steps.length === 0
        ? ApiModels.WORKFLOW_TRIGGERS.PRODUCT_PURCHASED
        : ApiModels.WORKFLOW_TRIGGERS.ALL_DEPENDENCIES;
      const { data: newTrigger } = await createStepFn({
        id: workflow.id,
        stepData: {
          data: {},
          dependentOn: [{ id: stepId }],
          eventName: triggerToCreate,
          title: null,
          type: ApiModels.StepType.TRIGGER,
        },
      });
      trigger = newTrigger;
    }
    if (trigger) {
      await createStepFn({
        id: workflow.id,
        stepData: {
          data: {},
          dependentOn: [trigger],
          eventName: ApiModels.WORKFLOW_ACTIONS.SEND_MESSAGE,
          title: null,
          type: ApiModels.StepType.ACTION,
        },
      });
    }
    await refetchWorkflow();
    setAddingStepAfterId(null);
  };

  const handleDeleteStep = (stepId: string) => async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const confirmation = window.confirm("Are you sure you want to delete this step?");

    if (!confirmation) {
      return;
    }

    setDeletingStep(stepId);
    let isFirstStep = false;
    let title;
    let nextTriggerStepId;

    workflow.threads.forEach((thread) => {
      if(stepId === thread.stepIds[1] && thread.stepIds.length > 2) {
        isFirstStep = true;
        title = workflow.steps.find(step => step.id === thread.stepIds[0])?.title;
        // eslint-disable-next-line prefer-destructuring
        nextTriggerStepId = thread.stepIds[2];
      }
    });

    if(isFirstStep && nextTriggerStepId && title) {
      await updateWorkflowStepFn({
        id: workflow.id,
        stepId: nextTriggerStepId,
        updates: {
          title,
        },
      });
    }

    await deleteStepFn({
      id: workflow.id,
      stepId,
    });

    await refetchWorkflow();
  };

  const getContextTypeLabel = (type: CONTEXT_TYPE): string => {
    if (type === CONTEXT_TYPE.PERSON) {
      return 'Person';
    }
    if (type === CONTEXT_TYPE.ADDITIONAL_INFO) {
      return 'Text';
    }
    if (type === CONTEXT_TYPE.SELECTION) {
      return 'Selection';
    }
    return 'Context';
  };

  const handleOwnerSelect = async (personIds: string[]): Promise<void> => {
    setOwnerPersonIds(personIds);
    await updateWorkflowOwnersFn({
      personIds,
      workflowId: workflow.id,
    });
  };

  const handleUpdateInvoiceConditions = async (workflowInvoiceId: string, metaData:{
    workflowInvoiceAssigneeIds?:string[];
    expressionId?:string
  }): Promise<void> => {

    await updateWorkflowInvoiceFn({
      data: { expressionId: metaData?.expressionId,
        workflowInvoiceAssigneeIds: metaData?.workflowInvoiceAssigneeIds,
      },
      id: workflowInvoiceId,
      workflowId: workflow.id,
    });
    refetchWorkflow();
  };

  const handleUpdateContextConditions = async (expressionId: string, contextId: string): Promise<void> => {
    await updateWorkflowContextFn({
      contextId,
      updates: { expressionId },
      workflowId: workflow.id,
    });
    refetchWorkflow();
  };

  return (
    <>
      <div className={s.editWorkflowForm}>
        <CollapsibleArea title="Settings" Icon={{ Component: Gear }} className={s.details}>
          <div className={s.editWorkflowFormHeader}>
            <FieldLabel id="description">
              Workflow name
              <Spacer size={4} />
              <TextInput disabled={!canEdit} placeholder={'Add workflow name'} id="name" value={workflow.name} onChange={handleChangeName} hasFixedHeight={false} />
            </FieldLabel>
            <Spacer size={8} />
            <CustomSelect
              label="Select Workflow Type"
              value={workflow.type}
              onChange={value => handleChangeType(value)}
              id="workflowType"
              options={workflowTypeOptions}
              disabled={!canEdit}
            />
            {canEditNoodleApplicationFees && (
              <>
                <Spacer size={16} />
                <PriceInput
                  currency={ApiModels.Currency.Usd}
                  value={workflow.noodleFee ?? 0}
                  name={'noodleFee'}
                  onChange={newValue => handleChangeWorkflowNumberField('noodleFee', newValue)}
                  label="Glade Fee"
                  disabled={!canEdit}
                />
              </>
            )}
            {canEditNoodleApplicationFees && (
              <>
                <Spacer size={16} />
                <PriceInput
                  currency={ApiModels.Currency.Usd}
                  value={workflow?.estimatedTotalFees ?? 0}
                  name={'estimatedTotalFees'}
                  onChange={newValue => handleChangeWorkflowNumberField('estimatedTotalFees', newValue)}
                  label="Estimated Total Fees"
                  disabled={!canEdit}
                />
              </>
            )}
            <Spacer size={16} />
            <CheckBox
              isChecked={workflow.hasGeneratedPdfs}
              label={'Needs to generate PDFs'}
              onChange={handleToggleHasGeneratedPdfs}
              className={s.checkBoxRow}
              disabled={!canEdit}
            />
            <CheckBox
              isChecked={workflow.sendEmailToCreatorOnCompletion}
              label={'Send email to team upon completion'}
              onChange={handleToggleSendEmailToCreatorOnCompletion}
              className={s.checkBoxRow}
              disabled={!canEdit}
            />
            <CheckBox
              isChecked={workflow.sendEmailToCreatorOnFirstPayment}
              label={'Send email to team on first payment'}
              onChange={handleToggleSendEmailToCreatorOnFirstPayment}
              className={s.checkBoxRow}
              disabled={!canEdit}
            />
            <CheckBox
              isChecked={workflow.canInitiateAssociatedWorkflows}
              label={'Can initiate associated cases'}
              onChange={handleToggleCanInitiateAssociatedWorkflows}
              className={s.checkBoxRow}
              disabled={!canEdit}
            />
            {isAttorney(creator?.businessCategory) && isUscisEnabled && (
              <CheckBox
                isChecked={workflow.enableUscisUpdates}
                label={'Enable USCIS Updates'}
                onChange={handleToggleEnableUscisUpdates}
                className={s.checkBoxRow}
                disabled={!canEdit}
              />
            )}
            {isAttorney(creator?.businessCategory) && isPacerEnabled && (
              <CheckBox
                isChecked={workflow.enablePacerSubmissions}
                label={'Enable PACER submissions'}
                onChange={handleToggleEnablePacerSubmissions}
                className={s.checkBoxRow}
                disabled={!canEdit}
              />
            )}
            {canEdit && <>
              <Spacer size={16} />
              <Button size="sm" variant="primary" loading={isSaving} onClick={handleSave}>
                Save
              </Button>
            </>}
          </div>
        </CollapsibleArea>
        <CollapsibleArea title="Contexts" Icon={{ Component: BracketsCurly }} className={s.details}>
          {workflow.contexts.length > 0 && (
            <>
              <Spacer />
              <Table>
                <THead>
                  <TableRow>
                    <TH label='Label' />
                    <TH label="Type" />
                    <TH label="Custom terms variable" />
                    <TH label="Options" />
                    <TH label="Is Title Field" />
                    <TH label="Is Optional" />
                    <TH label="Enable Invoice View" />
                    <TH label="Conditions" />
                    {canEdit && <TH>&nbsp;</TH>}
                  </TableRow>
                </THead>
                <TBody>
                  {workflow.contexts.map(context => (
                    <TableRow key={context.id}>
                      <TD label={context.label} compact />
                      <TD label={getContextTypeLabel(context.type)} compact />
                      <TD label={context.variables.length > 0 ? context.variables.map(v => v.variableName).join(', ') : '-'} compact />
                      <TD>
                        <div className={s.cellIsColumn}>
                          {context.type === CONTEXT_TYPE.SELECTION
                            ? (
                              <>
                                {context.options.map(option => (
                                  <span key={option.slug} className='caption'>{option.label}</span>
                                ))}
                              </>
                            )
                            : ( <span className='caption'>N/A</span> )}
                        </div>
                      </TD>
                      <TD>
                        <CheckBox
                          id={context.id}
                          isChecked={context.isTitleField}
                          label={''}
                          onChange={value => {
                            handleToggleIsTitleField(context.id, value);
                          }}
                          size="sm"
                          disabled={!canEdit}
                        />
                      </TD>
                      <TD>
                        <CheckBox
                          id={context.id}
                          isChecked={context.isOptional}
                          label={''}
                          onChange={value => {
                            handleToggleIsOptional(context.id, value);
                          }}
                          size="sm"
                          disabled={!canEdit}
                        />
                      </TD>
                      <TD>
                        {context.type === CONTEXT_TYPE.PERSON
                          ? (
                            <CheckBox
                              id={context.id}
                              isChecked={context.enableInvoiceView}
                              label={''}
                              onChange={value => {
                                handleToggleEnableInvoiceView(context.id, value);
                              }}
                              size="sm"
                              disabled={!canEdit}
                            />
                          )
                          : (
                            <span>-</span>
                          )}
                      </TD>
                      <TD>
                        <ConditionsWidget
                          conditionalExpression={context.expression || null}
                          options={workflow.contexts.filter((c) => c.id !== context.id).map((otherContext) => ({
                            key: otherContext.slug || otherContext.id,
                            label: otherContext.label,
                          }))}
                          type={'workflow-context'}
                          onOpenModal={() => {}}
                          handleSubmit={(expressionId) => handleUpdateContextConditions(expressionId , context.id)}
                        />

                      </TD>
                      {canEdit && <TD>
                        <Button
                          variant='destructive'
                          size='xs'
                          onClick={() => deleteContext(context.id)}
                          loading={deletingContextId === context.id}
                        >
                          Remove context
                        </Button>
                      </TD>}
                    </TableRow>
                  ))}
                </TBody>
              </Table>
            </>
          )}
          {canEdit && (
            <div className={s.padding}>
              <Button size="sm" variant="primary" onClick={() => setAddContextSectionOpen(true)}>
                Add Context
              </Button>
            </div>
          )}
          {isAddContextSectionOpen && (
            <Modal title="Add Context" onClose={() => setAddContextSectionOpen(false)}>
              <div className={s.padding}>
                <Spacer size={12} />
                <AddContextForm onSubmit={handleAddContext} workflowId={workflow.id} />
              </div>
            </Modal>
          )}
        </CollapsibleArea>
        <CollapsibleArea title="Invoices" Icon={{ Component: HandCoins }} className={s.details}>
          <Spacer />
          {workflow.workflowInvoices.length === 0 && !isAddInvoiceSectionOpen && (
            <div className={s.padding}>
              <span className={'caption'}>No invoices yet</span>
              <Spacer size={8} />
            </div>
          )}
          {workflow.workflowInvoices.length > 0 && (
            <WorkflowInvoices
              refetchWorkflow={refetchWorkflow}
              handleUpdateConditions={handleUpdateInvoiceConditions}
              invoices={workflow.workflowInvoices.map(invoice => ({
                expression: invoice.expression,
                id: invoice.id,
                referenceId: invoice.id,
                title: invoice.title || '',
                workflowInvoiceAssigneeIds: invoice?.workflowInvoiceAssigneeIds ?? [],
              }))}
              workflowId={workflow.id}
              removeInvoice={handleDeleteInvoice}
              workflowContexts={workflow.contexts}
              availableAssignees={[
                {
                  key: 'customer',
                  label: 'Contact',
                  type: 'customer' as AssignToPersonType,
                },
                ...workflow.contexts
                  .filter(c => c.type === CONTEXT_TYPE.PERSON)
                  .map(context => ({
                    key: context.slug,
                    label: context.label,
                    type: 'context' as AssignToPersonType,
                  })),
                ...creatorTeamMembers.map(teamMember => ({
                  key: teamMember.id,
                  label: teamMember.name || '',
                  type: 'team-member' as AssignToPersonType,
                })),
              ]}
            />
          )}
          {canEdit && (
            <div className={s.padding}>
              <Button size="sm" variant="primary" onClick={() => setIsAddInvoiceSectionOpen(!isAddInvoiceSectionOpen)}>
                Add Invoice
              </Button>
            </div>
          )}
          {isAddInvoiceSectionOpen && (
            <Modal title="Add Invoice" onClose={() => setIsAddInvoiceSectionOpen(false)}>
              <div className={s.padding}>
                <Spacer size={12} />
                <SelectInvoiceTemplate
                  onSelect={template => {
                    setCurrentInvoiceTemplateId(template.id);
                  }}
                />
                <Spacer />
                <Button
                  variant='primary'
                  fullWidth
                  size='md'
                  onClick={handleAddInvoice}
                  loading={creatingInvoice.isFetching}
                  disabled={!currentInvoiceTemplateId}
                >
                  Add Invoice
                </Button>
              </div>
            </Modal>
          )}
        </CollapsibleArea>
        <CollapsibleArea title="Owners" Icon={{ Component: Users }} className={s.details}>
          {isFetchingTeamMembers
            ? <><ProgressIndicator isCentered /><Spacer /></>
            : (
              <AssignCollaboratorsToAttachmentForm
                collaborators={teamMembers?.items?.map((m) => ({
                  color: m.primaryColour?.hex || null,
                  email: m.email || null,
                  id: m.id,
                  logo: m.image?.url || null,
                  name: m.name || null,
                })) ?? []}
                isSubmitting={isAssigningOwners}
                onSubmit={handleOwnerSelect}
                initialCollaboratorIds={ownerPersonIds}
              />
            )}
        </CollapsibleArea>
        {workflow.threads.map((thread, index) => (
          <div key={thread.stepIds[0]} className={s.thread}>
            <Buttons isWrapper
              className={s.threadLabel}
              onClick={() => {
                setCurrentStep(workflow.steps.find(step => step.id === thread.stepIds[0]) || null);
                setEditThreadTitleModalOpen(true);
              }}
              disabled={!canEdit}
            >
              {workflow.steps.find(step => step.id === thread.stepIds[0])?.title || 'Untitled Thread'}
            </Buttons>
            {thread.stepIds.map(stepId => workflow.steps.find(step => step.id === stepId)).filter(removeNullish).map((step) => (
              <div key={step.id}>
                {step.type === ApiModels.StepType.TRIGGER
                  ? (
                    <div className={s.step}>
                      <TriggerCard
                        canEdit={canEdit}
                        availableTriggers={getAvailableTriggers({ step, workflow })}
                        trigger={step}
                        updateStep={updateStep}
                        deleteStep={handleDeleteStep}
                        isDeleting={deletingStep === step.id}
                        workflowId={workflow.id}
                      />
                      <Spacer size={12} />
                      {index === 0 && (
                        <>
                          <WorkflowOptionSelect
                            currentOption={stepStatuses.get(step.id) || 'maintain-status'}
                            isAttorneyCase={true}
                            enableMaintainStatus={true}
                            onChange={e => handleWorkflowStatusChange(step.id, e.target.value as SelectOption)}
                            fullWidth
                            label="Switch workflow status to:"
                            statuses={customStatuses?.items}
                          />
                          <Spacer />
                        </>
                      )}
                    </div>
                  )
                  : (
                    <div className={s.stepTasks}>
                      <ActionCard
                        action={step}
                        canEdit={canEdit}
                        onEditClick={() => {
                          setCurrentStep(step);
                          setEditWorkflowStepModalOpen(true);
                        }}
                        onUpdate={refetchWorkflow}
                        actionData={step.data as SendMessageActionData}
                        availableAssignees={[
                          {
                            key: 'customer',
                            label: 'Contact',
                            type: 'customer' as AssignToPersonType,
                          },
                          ...workflow.contexts
                            .filter(c => c.type === CONTEXT_TYPE.PERSON)
                            .map(context => ({
                              key: context.id,
                              label: context.label,
                              type: 'context' as AssignToPersonType,
                            })),
                          ...creatorTeamMembers.map(teamMember => ({
                            key: teamMember.id,
                            label: teamMember.name || '',
                            type: 'team-member' as AssignToPersonType,
                          })),
                        ]}
                        workflowContexts={workflow.contexts.map(context => ({
                          id: context.id,
                          label: context.label,
                          slug: context.slug,
                        }))}
                        workflowId={workflow.id}
                      />
                    </div>
                  )}
                {step.type === ApiModels.StepType.ACTION && (
                  <>
                    <Spacer size={16} />
                    {canEdit && <>
                      <Buttons isWrapper isFullWidth onClick={handleAddSteps(step.id)} disabled={addingStepAfterId === step.id}>
                        <div className={s.sendMessage__button}>
                          {addingStepAfterId === step.id ? <ProgressIndicator size={20} /> : <PlusCircle fill="var(--color-gray-100)" size={20} weight="fill" />}
                          <strong className="body-sm-bold">Add Step</strong>
                        </div>
                      </Buttons>
                      <Spacer size={16} />
                    </>}
                  </>
                )}
              </div>
            ))}
          </div>
        ))}
        {canEdit && <>
          <Spacer size="16px" />
          <Buttons isWrapper onClick={() => setAddThreadModalOpen(true)}>
            <div className={s.sendMessage__button}>
              {<PlusCircle fill="var(--color-primary)" size={20} weight="fill" />}
              <strong className="body-sm-bold">Add Thread</strong>
            </div>
          </Buttons>
          <Spacer size={16} />
        </>}
      </div>
      {isEditWorkflowStepModalOpen && currentStep && (
        <EditWorkflowStepModal
          workflow={workflow}
          currentStep={currentStep}
          onSave={async () => {
            await refetchWorkflow();
            setEditWorkflowStepModalOpen(false);
            setCurrentStep(null);
          }}
          onClose={() => {
            setEditWorkflowStepModalOpen(false);
            setCurrentStep(null);
          }}
        />
      )}
      {isAddThreadModalOpen && (
        <AddWorkflowThreadModal
          workflow={{
            id: workflow.id,
            stepsLength: workflow.steps.length,
          }}
          onSubmit={async () => {
            await refetchWorkflow();
          }}
          onClose={() => setAddThreadModalOpen(false)}
        />
      )}
      {isEditThreadTitleModalOpen && currentStep && (
        <EditWorkflowThreadTitleModal
          currentStep={currentStep}
          workflowId={workflow.id}
          onSubmit={async () => {
            await refetchWorkflow();
          }}
          onClose={() => {
            setEditThreadTitleModalOpen(false);
            setCurrentStep(null);
          }}
        />
      )}
    </>
  );
};

export default EditWorkflowForm;
